import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

type AymoMotionState =
  | 'idle'
  | 'thinking'
  | 'coding'
  | 'designing'
  | 'api-action'
  | 'listening'
  | 'success'
  | 'error';

interface AymoBridge {
  version: number;
  setState(state: AymoMotionState, options?: { label?: string; progress?: number }): void;
  setColor(color: string): void;
  pause(): void;
  resume(): void;
}

@Component({
  selector: 'app-aymo-motion',
  standalone: true,
  template: `
    <section
      class="aymo-motion"
      [class.compact]="compact"
      [class.hidden-avatar]="appearance === 'hidden'"
      [attr.aria-label]="label || 'Aymo'"
    >
      @if (appearance !== 'hidden') {
        <iframe
          #frame
          src="/aymo-motion/v1.2.0/embed.html"
          title="Aymo canlı animasyonu"
          loading="lazy"
          tabindex="-1"
          (load)="frameLoaded()"
        ></iframe>
      }
      @if (customizable) {
        <button
          class="appearance-button"
          type="button"
          aria-label="Aymo görünümünü özelleştir"
          [attr.aria-expanded]="settingsOpen"
          (click)="settingsOpen = !settingsOpen"
        >⚙</button>
        @if (settingsOpen) {
          <div class="appearance-panel">
            <strong>Aymo görünümü</strong>
            <label>
              <span>Renk</span>
              <input type="color" [value]="color" (input)="setColor($event)" />
            </label>
            <div class="color-presets" aria-label="Hazır renkler">
              @for (preset of presets; track preset) {
                <button
                  type="button"
                  [style.background]="preset"
                  [attr.aria-label]="'Aymo rengini ' + preset + ' yap'"
                  (click)="chooseColor(preset)"
                ></button>
              }
            </div>
            <label>
              <span>Görünüm</span>
              <select [value]="appearance" (change)="setAppearance($event)">
                <option value="always">Her zaman göster</option>
                <option value="working">Çalışırken canlandır</option>
                <option value="hidden">Gizle</option>
              </select>
            </label>
          </div>
        }
      }
      <span class="sr-only" aria-live="polite">{{ label || stateLabel() }}</span>
    </section>
  `,
  styles: [
    `
      :host{display:block;min-width:0}.aymo-motion{height:100%;min-height:180px;position:relative;width:100%}.aymo-motion iframe{border:0;display:block;height:100%;min-height:inherit;pointer-events:none;width:100%}.aymo-motion.compact{min-height:112px}.aymo-motion.hidden-avatar{min-height:38px}.appearance-button{align-items:center;backdrop-filter:blur(10px);background:color-mix(in srgb,var(--surface,#fff) 82%,transparent);border:1px solid var(--border,#d8dde5);border-radius:50%;bottom:6px;color:var(--text,#172033);cursor:pointer;display:flex;font:inherit;height:34px;justify-content:center;position:absolute;right:6px;width:34px;z-index:3}.appearance-panel{background:var(--surface,#fff);border:1px solid var(--border,#d8dde5);border-radius:16px;bottom:46px;box-shadow:0 18px 50px #0002;color:var(--text,#172033);display:grid;gap:11px;padding:14px;position:absolute;right:6px;width:210px;z-index:4}.appearance-panel label{align-items:center;display:flex;font-size:.78rem;gap:10px;justify-content:space-between}.appearance-panel input[type=color]{background:transparent;border:0;height:32px;padding:0;width:44px}.appearance-panel select{background:var(--surface,#fff);border:1px solid var(--border,#d8dde5);border-radius:9px;color:inherit;font:inherit;max-width:128px;padding:7px}.color-presets{display:flex;gap:7px}.color-presets button{border:2px solid #fff;border-radius:50%;box-shadow:0 0 0 1px #0003;height:22px;width:22px}.sr-only{clip:rect(0,0,0,0);clip-path:inset(50%);height:1px;overflow:hidden;position:absolute;white-space:nowrap;width:1px}@media(prefers-reduced-motion:reduce){.aymo-motion iframe{opacity:.92}}@media(max-width:560px){.appearance-panel{position:fixed;bottom:82px;right:14px;width:min(260px,calc(100vw - 28px))}}
    `,
  ],
})
export class AymoMotionComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('frame') private frame?: ElementRef<HTMLIFrameElement>;
  @Input() state: AymoMotionState = 'idle';
  @Input() label = '';
  @Input() progress?: number;
  @Input() compact = false;
  @Input() customizable = false;

  protected settingsOpen = false;
  protected color = '#5865f2';
  protected appearance: 'always' | 'working' | 'hidden' = 'always';
  protected readonly presets = ['#5865f2', '#00a884', '#ff477e', '#ff9f1c', '#7c3aed'];
  private readyTimer?: ReturnType<typeof setInterval>;
  private destroyed = false;
  private readonly visibilityHandler = () => this.syncVisibility();

  constructor() {
    if (typeof localStorage !== 'undefined') {
      const savedColor = localStorage.getItem('aymudo.aymoMotion.color');
      const savedAppearance = localStorage.getItem('aymudo.aymoMotion.appearance');
      if (/^#[0-9a-f]{6}$/i.test(savedColor || '')) this.color = savedColor!;
      if (savedAppearance === 'working' || savedAppearance === 'hidden')
        this.appearance = savedAppearance;
    }
  }

  ngAfterViewInit() {
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  ngOnChanges(_: SimpleChanges) {
    this.sync();
  }

  protected frameLoaded() {
    clearInterval(this.readyTimer);
    let attempts = 0;
    this.readyTimer = setInterval(() => {
      attempts += 1;
      if (this.bridge()) {
        clearInterval(this.readyTimer);
        this.sync();
      } else if (attempts >= 80) clearInterval(this.readyTimer);
    }, 100);
  }

  protected setColor(event: Event) {
    this.chooseColor((event.target as HTMLInputElement).value);
  }

  protected chooseColor(value: string) {
    if (!/^#[0-9a-f]{6}$/i.test(value)) return;
    this.color = value;
    if (typeof localStorage !== 'undefined') localStorage.setItem('aymudo.aymoMotion.color', value);
    this.bridge()?.setColor(value);
  }

  protected setAppearance(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    if (value !== 'always' && value !== 'working' && value !== 'hidden') return;
    this.appearance = value;
    if (typeof localStorage !== 'undefined') localStorage.setItem('aymudo.aymoMotion.appearance', value);
    this.settingsOpen = false;
    queueMicrotask(() => this.sync());
  }

  protected stateLabel() {
    return ({ idle: 'Aymo hazır', thinking: 'Aymo düşünüyor', coding: 'Aymo çalışıyor', designing: 'Aymo tasarlıyor', 'api-action': 'Aymo işlemi uyguluyor', listening: 'Aymo dinliyor', success: 'Aymo tamamladı', error: 'Aymo bir sorunla karşılaştı' } as Record<string,string>)[this.state] || 'Aymo';
  }

  private bridge(): AymoBridge | undefined {
    try {
      return (this.frame?.nativeElement.contentWindow as Window & { Aymo?: AymoBridge })?.Aymo;
    } catch {
      return undefined;
    }
  }

  private sync() {
    const bridge = this.bridge();
    if (!bridge || this.destroyed) return;
    bridge.setColor(this.color);
    const visualState = this.appearance === 'working' && this.state === 'idle' ? 'idle' : this.state;
    const options: {label:string;progress?:number} = { label: this.label || this.stateLabel() };
    if(typeof this.progress==='number'&&Number.isFinite(this.progress))options.progress=Math.max(0,Math.min(1,this.progress));
    bridge.setState(visualState, options);
    this.syncVisibility();
  }

  private syncVisibility() {
    const bridge = this.bridge();
    if (!bridge) return;
    if (
      (typeof document !== 'undefined' && document.hidden) ||
      (this.appearance === 'working' && this.state === 'idle')
    )
      bridge.pause();
    else bridge.resume();
  }

  ngOnDestroy() {
    this.destroyed = true;
    clearInterval(this.readyTimer);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.bridge()?.pause();
  }
}
